/**
 * A2G Box Builder — savings reveal
 *
 * Selections live in sessionStorage. "Max price stack" blocks define flat
 * bundle prices keyed by item count (3 -> 2299). Every figure shown is derived
 * from the customer's actual selected variant prices — never from compare-at /
 * MRP, which would be both dishonest and a compliance risk.
 *
 * Two bundle modes, set per section:
 *
 *   Dynamic (default) — the picks themselves go into the cart carrying
 *     _Box / _BoxId, and a discount function allocates the flat price across
 *     them. The bar sells the saving: count-up, percentage, marginal card
 *     pricing, upsell nudges.
 *
 *   Static — the tier's own bundle product is the single cart line, at its own
 *     price, with the picks attached as visible line-item properties. The bar
 *     becomes a calculator: which flat price does this many items buy. All the
 *     derived-savings furniture is switched off, because the number on screen
 *     is no longer being argued for — it simply is the price.
 *
 * Reveal timeline (460ms total):
 *   0-120   slot fill
 *   60-300  plate flip (border-style switches instantly, colour eases)
 *   120-420 count-up, struck total fades in over the last 120ms
 *   240-400 CTA arms
 *   300-460 nudge enters
 *   480     announce, once, politely
 */
(function () {
  const STORAGE_KEY = 'box-builder-selection';
  const ADDED_KEY = 'box-builder-added';
  const BUNDLE_KEY = 'box-builder-bundle';

  const T_PLATE_FLIP = 60;
  const T_COUNT_START = 120;
  const T_CTA_ARM = 240;
  const T_NUDGE_IN = 300;
  const COUNT_REVEAL_MS = 300;
  const COUNT_UPDATE_MS = 220;
  const COUNT_REMOVE_MS = 160;
  const ANNOUNCE_DEBOUNCE_MS = 500;

  const CONFETTI_COLOURS = ['#f7d83c', '#1c1c1c', '#ffffff', '#e5c735'];
  const CONFETTI_MS = 1400;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Two bursts from the bottom corners, matching the original base's geometry.
   * Self-contained rather than the old jsDelivr canvas-confetti tag: no
   * third-party request, nothing to break under a strict CSP, and the palette
   * follows the brand. Purely decorative — no meaning lives here, so it is
   * skipped entirely for prefers-reduced-motion.
   */
  function launchConfetti() {
    if (prefersReducedMotion()) return;

    const previous = document.querySelector('[data-box-confetti]');
    if (previous) previous.remove();

    const width = window.innerWidth;
    const height = window.innerHeight;
    const canvas = document.createElement('canvas');
    canvas.dataset.boxConfetti = '';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:10';

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = width * ratio;
    canvas.height = height * ratio;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    document.body.appendChild(canvas);

    const particles = [];
    const burst = (originX, angleDeg) => {
      for (let i = 0; i < 60; i += 1) {
        const angle = (angleDeg + (Math.random() - 0.5) * 55) * (Math.PI / 180);
        const speed = 7 + Math.random() * 7;
        particles.push({
          x: originX,
          y: height * 0.85,
          vx: Math.cos(angle) * speed,
          vy: -Math.sin(angle) * speed,
          w: 5 + Math.random() * 5,
          h: 3 + Math.random() * 4,
          colour: CONFETTI_COLOURS[Math.floor(Math.random() * CONFETTI_COLOURS.length)],
          rot: Math.random() * Math.PI,
          vrot: (Math.random() - 0.5) * 0.3,
        });
      }
    };

    burst(0, 60);
    burst(width, 120);

    const start = performance.now();
    const frame = (now) => {
      const elapsed = now - start;
      const alpha = Math.max(0, 1 - elapsed / CONFETTI_MS);
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.vy += 0.22; // gravity
        p.vx *= 0.99; // drag
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vrot;

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.colour;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });

      if (elapsed < CONFETTI_MS) requestAnimationFrame(frame);
      else canvas.remove();
    };

    requestAnimationFrame(frame);
  }

  class BoxBuilder {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.addBtnLabel = root.dataset.addBtnLabel || 'Add to Box';
      this.currency = window.themeVariables?.settings?.cartCurrency || 'INR';
      this.showNudgeDesktop = root.dataset.showNudgeDesktop !== 'false';
      this.showNudgeMobile = root.dataset.showNudgeMobile !== 'false';
      this.showPct = root.dataset.showPct !== 'false';
      this.confettiMode = root.dataset.confetti || 'tier';
      this.staticMode = root.dataset.bundleMode === 'static';

      this.stacks = this.loadStacks();
      this.hasStacks = this.stacks.length > 0;

      if (this.hasStacks) {
        this.minItems = this.stacks[0].count;
        this.maxItems = this.stacks[this.stacks.length - 1].count;
      } else {
        this.minItems = parseInt(root.dataset.minItems, 10) || 3;
        this.maxItems = parseInt(root.dataset.maxItems, 10) || 5;
      }

      this.cards = Array.from(root.querySelectorAll('[data-box-card]'));
      this.bar = root.querySelector('[data-box-bar]');
      this.slots = Array.from(root.querySelectorAll('[data-box-slot]'));
      this.cartBtn = root.querySelector('[data-box-cart-btn]');
      this.cartBtnLabel = root.querySelector('[data-cart-btn-label]');
      this.cartBtnLabelShort = root.querySelector('[data-cart-btn-label-short]');
      this.statusEl = root.querySelector('[data-box-status]');
      this.errorEl = root.querySelector('[data-box-error]');

      this.plate = root.querySelector('[data-box-plate]');
      this.plateLocked = root.querySelector('[data-plate-locked]');
      this.plateKicker = root.querySelector('[data-plate-kicker]');
      this.plateAmount = root.querySelector('[data-plate-amount]');
      this.platePct = root.querySelector('[data-plate-pct]');
      this.plateStrike = root.querySelector('[data-plate-strike]');
      this.plateProof = root.querySelector('[data-plate-proof]');
      this.plateProofText = root.querySelector('[data-plate-proof-text]');

      this.nudge = root.querySelector('[data-box-nudge]');
      this.nudgeTitle = root.querySelector('[data-box-nudge-title]');
      this.nudgeSub = root.querySelector('[data-box-nudge-sub]');

      this._displayedAmount = 0;
      this._countUpFrame = null;
      this._timers = [];
      this._prevState = null;
      this._prevCount = 0;
      this._ctaCondensed = false;
      this._syncing = false;

      this.selection = this.loadState();
      this.addedToCart = this.loadAddedToCart();
      this.bundle = this.staticMode ? this.loadBundle() : null;
      this.reconcileSelection();

      this.bindCardButtons();
      this.bindSlotButtons();
      this.cartBtn.addEventListener('click', () => this.handleCta());
      if (this.nudge) this.nudge.addEventListener('click', () => this.handleNudge());

      this.bindCartEvents();
      this.lockAmountWidth();
      this.observeBar();
      this.render({ initial: true });

      // Anything removed while we were off this page needs catching up on.
      if (this.addedToCart.length > 0 || this.bundle) this.fetchAndSyncCart();
    }

    // ---------- cart sync ----------

    /**
     * The cart drawer, cart page and the theme's own pageshow handler all
     * announce changes on documentElement. Listening lets a line removed in the
     * drawer fall back out of the stack instead of leaving a slot showing a
     * tick for something that is no longer in the cart.
     */
    bindCartEvents() {
      const handler = (event) => {
        const cart = event && event.detail && event.detail.cart;
        if (cart && Array.isArray(cart.items)) this.syncWithCart(cart.items);
        else this.fetchAndSyncCart();
      };

      document.documentElement.addEventListener('cart:updated', handler);
      document.documentElement.addEventListener('cart:refresh', handler);
    }

    async fetchAndSyncCart() {
      if (this._syncing) return;
      try {
        const cart = await (await fetch(`${this.cartUrl()}.js`)).json();
        if (Array.isArray(cart.items)) this.syncWithCart(cart.items);
      } catch (e) {
        // Leave the stored state alone rather than guessing.
      }
    }

    /**
     * Drops anything we put in the cart that the customer has since removed.
     * Matches on the _BoxId we wrote when bundling, so an identical product
     * added from a product page is never mistaken for our line — and when we
     * cannot tell them apart we under-remove rather than silently discarding
     * something the customer still has.
     */
    syncWithCart(items) {
      if (this._syncing) return;

      // Static mode: one line to look for, matched on its own _BoxId so a
      // second stack of the same size is never mistaken for this one. Gone
      // from the cart means gone from the widget — the whole stack, since the
      // line is indivisible.
      if (this.bundle) {
        const stillThere = items.some(
          (line) =>
            String(line.variant_id || line.id) === String(this.bundle.variantId) &&
            line.properties &&
            line.properties._BoxId === this.bundle.boxId
        );
        if (stillThere) return;

        this.bundle = null;
        this.selection = [];
        this.saveBundle();
        this.saveState();
        this.render({ removed: true });
        return;
      }

      if (this.addedToCart.length === 0) return;

      const present = new Set();
      items.forEach((line) => {
        const id = String(line.variant_id || line.id);
        present.add(id);
        const boxId = line.properties && line.properties._BoxId;
        if (boxId) present.add(`${id}::${boxId}`);
      });

      const stale = this.addedToCart.filter((id) => {
        const entry = this.selection.find((i) => String(i.variantId) === id);
        const boxId = entry && entry.boxId;
        return boxId ? !present.has(`${id}::${boxId}`) : !present.has(id);
      });

      if (stale.length === 0) return;

      const staleIds = new Set(stale);
      this.selection = this.selection.filter((i) => !staleIds.has(String(i.variantId)));
      this.addedToCart = this.addedToCart.filter((id) => !staleIds.has(id));
      this.saveState();
      this.saveAddedToCart();
      this.render({ removed: true });
    }

    // ---------- state ----------

    loadStacks() {
      const el = this.root.querySelector('[data-box-stacks]');
      if (!el) return [];
      try {
        const parsed = JSON.parse(el.textContent);
        if (!Array.isArray(parsed)) return [];
        return parsed
          .filter((s) => Number.isFinite(s.count) && Number.isFinite(s.price) && s.count > 0)
          .sort((a, b) => a.count - b.count);
      } catch (e) {
        return [];
      }
    }

    loadState() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        return [];
      }
    }

    saveState() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.selection));
      } catch (e) {
        /* Safari private mode — carry on in memory. */
      }
    }

    loadAddedToCart() {
      try {
        const raw = sessionStorage.getItem(ADDED_KEY);
        return raw ? JSON.parse(raw).map(String) : [];
      } catch (e) {
        return [];
      }
    }

    saveAddedToCart() {
      try {
        sessionStorage.setItem(ADDED_KEY, JSON.stringify(this.addedToCart));
      } catch (e) {
        /* See saveState. */
      }
    }

    /** The static-mode bundle line we put in the cart, if there is one. */
    loadBundle() {
      try {
        const raw = sessionStorage.getItem(BUNDLE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed || typeof parsed !== 'object' || !parsed.boxId || !parsed.variantId) {
          return null;
        }
        return parsed;
      } catch (e) {
        return null;
      }
    }

    saveBundle() {
      try {
        if (this.bundle) sessionStorage.setItem(BUNDLE_KEY, JSON.stringify(this.bundle));
        else sessionStorage.removeItem(BUNDLE_KEY);
      } catch (e) {
        /* See saveState. */
      }
    }

    /** Wipe the widget back to an empty stack. The cart is left alone. */
    startNewStack() {
      this.selection = [];
      this.addedToCart = [];
      this.bundle = null;
      this.saveState();
      this.saveAddedToCart();
      this.saveBundle();
    }

    /** Drop stored items whose card is gone, and re-read prices from the DOM. */
    reconcileSelection() {
      const known = new Map(this.cards.map((c) => [String(c.dataset.variantId), c]));
      const before = this.selection.length;

      this.selection = this.selection
        .filter((item) => known.has(String(item.variantId)))
        .slice(0, this.maxItems)
        .map((item) => {
          const card = known.get(String(item.variantId));
          return {
            variantId: String(item.variantId),
            title: card.dataset.productTitle,
            image: card.dataset.productImage,
            price: card.dataset.productPrice,
            // Carried through so cart sync can still match the exact line
            // after a page load.
            boxId: item.boxId,
          };
        });

      if (this.selection.length !== before) this.saveState();

      // A stored bundle line describes an exact set of picks. If the section no
      // longer offers all of them, the widget can no longer faithfully show
      // what is in the cart, so it stops claiming to. The cart line stays.
      if (this.bundle && this.selection.length !== this.bundle.count) {
        this.bundle = null;
        this.saveBundle();
      }
    }

    getNewItems() {
      return this.selection.filter((i) => !this.addedToCart.includes(String(i.variantId)));
    }

    /**
     * Static mode only: the stack on screen has already been bought, either as
     * the bundle product or — on the cheapest path — as plain singles.
     *
     * It matters because the two static routes add different things. Letting a
     * 4th pick join a stack whose 3 singles are already in the cart would arm
     * the CTA to add a "Buy 4" bundle product on top of them, and the customer
     * would pay for both. Committed stacks are therefore frozen: the next pick
     * starts a new one.
     */
    isCommitted() {
      if (!this.staticMode) return false;
      if (this.bundle) return true;
      return this.selection.length > 0 && this.getNewItems().length === 0;
    }

    isSelected(variantId) {
      const id = String(variantId);
      return this.selection.some((i) => String(i.variantId) === id);
    }

    slotIndexOf(variantId) {
      const id = String(variantId);
      return this.selection.findIndex((i) => String(i.variantId) === id);
    }

    // ---------- pricing ----------

    priceOf(card) {
      return parseInt(card.dataset.productPrice, 10) || 0;
    }

    listTotal() {
      return this.selection.reduce((sum, i) => sum + (parseInt(i.price, 10) || 0), 0);
    }

    stackFor(count) {
      return this.stacks.find((s) => s.count === count) || null;
    }

    nextStackAfter(count) {
      return this.stacks.find((s) => s.count > count) || null;
    }

    /** Prices of everything not yet chosen, priciest first. */
    availablePrices() {
      const chosen = new Set(this.selection.map((i) => String(i.variantId)));
      return this.cards
        .filter((c) => !chosen.has(String(c.dataset.variantId)))
        .map((c) => this.priceOf(c))
        .sort((a, b) => b - a);
    }

    /**
     * Saving reachable at a given tier if the customer fills the remaining
     * slots with the priciest products left. Always a real, buyable figure.
     */
    projectedSavingAt(stack) {
      if (!stack) return null;
      const needed = stack.count - this.selection.length;
      if (needed < 0) return null;
      const available = this.availablePrices();
      if (needed > available.length) return null;
      const total = this.listTotal() + available.slice(0, needed).reduce((s, p) => s + p, 0);
      return total - stack.price;
    }

    /** Tier-to-tier delta: what the next item genuinely costs. */
    marginalCost() {
      const current = this.stackFor(this.selection.length);
      const next = this.nextStackAfter(this.selection.length);
      if (!current || !next) return null;
      return next.price - current.price;
    }

    /** Below the first tier, the next item costs whatever closes the gap. */
    costToReachTier() {
      const next = this.nextStackAfter(this.selection.length);
      if (!next || this.selection.length >= this.minItems) return null;
      return Math.max(next.price - this.listTotal(), 0);
    }

    /**
     * The shortest route out of a losing combination: swap the cheapest chosen
     * items for the priciest ones left, one at a time, and stop at the first
     * count that actually turns a saving. Null when no swap can win.
     */
    bestSwap() {
      const stack = this.stackFor(this.selection.length);
      if (!stack || this.selection.length === 0) return null;

      const chosen = this.selection
        .map((i) => parseInt(i.price, 10) || 0)
        .sort((a, b) => a - b); // cheapest first
      const available = this.availablePrices(); // priciest first
      if (!available.length) return null;

      let total = this.listTotal();
      const limit = Math.min(chosen.length, available.length);

      for (let k = 1; k <= limit; k += 1) {
        // Once the incoming pick is no better than the outgoing one, no
        // further swap can improve the total.
        if (available[k - 1] <= chosen[k - 1]) break;
        total = total - chosen[k - 1] + available[k - 1];
        const saving = total - stack.price;
        if (saving > 0) return { pick: available[0], saving, swaps: k };
      }

      return null;
    }

    formatMoney(minorUnits, decimals) {
      const amount = (minorUnits || 0) / 100;
      const digits = typeof decimals === 'number' ? decimals : 0;
      try {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        }).format(amount);
      } catch (e) {
        return '₹' + Math.round(amount).toLocaleString('en-IN');
      }
    }

    /**
     * Reserve the display slot at the widest figure it will ever hold, so
     * counting from ₹0 to ₹1,596 never nudges the proof line sideways.
     */
    lockAmountWidth() {
      if (!this.plateAmount || !this.hasStacks) return;
      const all = this.cards.map((c) => this.priceOf(c)).sort((a, b) => b - a);
      const candidates = [];

      this.stacks.forEach((stack) => {
        candidates.push(stack.price);
        const top = all.slice(0, stack.count).reduce((s, p) => s + p, 0);
        candidates.push(top);
        candidates.push(Math.abs(top - stack.price));
      });

      const widest = candidates.reduce(
        (max, v) => Math.max(max, this.formatMoney(v).length),
        0
      );
      this.plateAmount.style.minWidth = `${widest}ch`;
    }

    // ---------- interactions ----------

    bindCardButtons() {
      this.cards.forEach((card) => {
        const btn = card.querySelector('[data-box-add-btn]');
        if (!btn) return;
        btn.addEventListener('click', () => {
          if (this.isSelected(card.dataset.variantId)) {
            // A stack that has already been bought is edited in the cart, not
            // here.
            if (this.isCommitted()) return;
            this.removeItem(card.dataset.variantId);
          } else {
            this.addItem(card);
          }
        });
      });
    }

    bindSlotButtons() {
      this.slots.forEach((slot) => {
        const btn = slot.querySelector('[data-box-slot-remove]');
        if (!btn) return;
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (slot.dataset.variantId) this.removeItem(slot.dataset.variantId, slot);
        });
      });
    }

    addItem(card) {
      // Static mode with a stack already bought: picking anything new is
      // unambiguously the start of stack #2, so treat it as one rather than
      // leaving a dead control. What is in the cart is untouched — each stack
      // prices independently, exactly as the nudge beside it promises.
      if (this.isCommitted()) this.startNewStack();

      if (this.selection.length >= this.maxItems) return;

      this.selection.push({
        variantId: String(card.dataset.variantId),
        title: card.dataset.productTitle,
        image: card.dataset.productImage,
        price: card.dataset.productPrice,
      });

      this.saveState();
      this.render({ added: true, filledIndex: this.selection.length - 1 });
    }

    removeItem(variantId, originSlot) {
      const id = String(variantId);
      this.selection = this.selection.filter((i) => String(i.variantId) !== id);
      this.addedToCart = this.addedToCart.filter((a) => a !== id);
      this.saveState();
      this.saveAddedToCart();
      this.render({ removed: true });

      if (originSlot) originSlot.focus({ preventScroll: true });
    }

    handleNudge() {
      const state = this._prevState;
      if (state === 'complete') return;

      if (state === 'postadd') {
        // "Build a 2nd stack" — the current one is already in the cart.
        this.startNewStack();
        this.render({ removed: true });
      }

      const target = this.cards.find((c) => !this.isSelected(c.dataset.variantId));
      if (!target) return;
      target.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'center',
      });
      const btn = target.querySelector('[data-box-add-btn]');
      if (btn) btn.focus({ preventScroll: true });
    }

    // ---------- render ----------

    clearTimers() {
      this._timers.forEach((t) => clearTimeout(t));
      this._timers = [];
    }

    after(ms, fn) {
      if (prefersReducedMotion() || ms === 0) {
        fn();
        return;
      }
      this._timers.push(setTimeout(fn, ms));
    }

    render(options) {
      const opts = options || {};
      this.clearTimers();
      this.clearError();

      const model = this.buildModel();

      // Read before renderPlate mutates the class.
      const wasUnlocked = this.plate ? this.plate.classList.contains('is-unlocked') : false;

      this.renderSlots(model, opts);
      this.renderCards(model);
      this.renderPlate(model, opts);
      this.renderCta(model, opts);
      this.renderNudge(model, opts);
      this.announce(model);
      this.maybeCelebrate(model, opts, wasUnlocked);
      this.fitPlate(model);

      this._prevState = model.state;
      this._prevCount = model.count;
    }

    /**
     * Font metrics and translated copy vary, so the mobile band is measured
     * rather than estimated. The band is a fixed 50/50 split, so neither half
     * can borrow width from the other; each has to shed its own least important
     * element instead.
     *
     *   CTA  — if its label will not fit its half, the price drops out of it.
     *          The price is already on the proof line in the plate beside it.
     *   Plate row 1 — if the kicker will not fit beside the % chip, the chip
     *          goes. The percentage is secondary; the kicker is what tells the
     *          customer whether the number is a saving, a total or a promise.
     */
    fitPlate(model) {
      if (!this.isCompact()) {
        this._ctaCondensed = false;
        return;
      }

      requestAnimationFrame(() => {
        if (this.cartBtn && this.cartBtnLabelShort) {
          const overflowing = this.cartBtn.scrollWidth > this.cartBtn.clientWidth + 1;

          if (overflowing && !this._ctaCondensed) {
            this._ctaCondensed = true;
            this.cartBtnLabelShort.textContent = this.tinyCtaLabel(model);
          } else if (!overflowing && this._ctaCondensed && !this.isTiny()) {
            // Only widen back with clear slack, so the labels cannot oscillate.
            const slack = this.cartBtn.clientWidth - this.cartBtn.scrollWidth;
            if (slack > 40) {
              this._ctaCondensed = false;
              this.cartBtnLabelShort.textContent = model.ctaShort;
            }
          }
        }

        // Last-resort guarantee that row 1 can never overlap, whatever the copy.
        // renderPlate re-shows the chip on every render, so this self-corrects.
        if (this.plateKicker && this.platePct && !this.platePct.hidden) {
          if (this.plateKicker.scrollWidth > this.plateKicker.clientWidth + 1) {
            this.platePct.hidden = true;
          }
        }
      });
    }

    /** Fires on the beat the number starts landing, not before it. */
    maybeCelebrate(model, opts, wasUnlocked) {
      if (this.confettiMode === 'off') return;
      if (!opts.added || opts.initial) return;
      if (model.locked || model.state === 'postadd') return;

      const crossedThreshold = !wasUnlocked;
      const reachedNewTier = model.count !== this._prevCount;
      const should =
        this.confettiMode === 'tier' ? reachedNewTier : crossedThreshold;

      if (should) this.after(T_COUNT_START, launchConfetti);
    }

    /** Single source of truth for all eight states. */
    buildModel() {
      const count = this.selection.length;
      const listTotal = this.listTotal();
      const stack = this.stackFor(count);
      const saving = stack ? listTotal - stack.price : null;
      const inCart = count > 0 && (Boolean(this.bundle) || this.getNewItems().length === 0);

      const m = {
        count,
        listTotal,
        stack,
        saving,
        locked: true,
        kicker: '',
        amount: null,
        pct: null,
        strike: null,
        proof: '',
        announce: null,
        ctaFull: '',
        ctaShort: '',
        ctaDisabled: true,
        nudgeTitle: null,
        nudgeSub: null,
        nudgeStatic: false,
        slotMode: 'remove',
        remaining: 0,
        state: 'empty',
      };

      if (!this.hasStacks) {
        // No tiers configured — behave like the original simple builder.
        m.state = count >= this.minItems ? 'threshold' : 'partial';
        m.remaining = Math.max(this.minItems - count, 0);
        m.ctaDisabled = count < this.minItems;
        m.ctaFull = m.ctaDisabled ? `Add ${this.minItems - count} more` : 'Add to cart';
        m.ctaShort = m.ctaFull;
        return m;
      }

      if (this.staticMode) return this.staticModel(m, inCart);

      const nextTier = this.nextStackAfter(count);
      const topAvailable = this.availablePrices()[0] || 0;
      const compact = this.isCompact();

      if (inCart) {
        // State 8 — the stack is already in the cart.
        m.state = 'postadd';
        m.slotMode = 'locked';
        m.ctaFull = 'Go to cart →';
        m.ctaShort = 'Go to cart';
        m.ctaDisabled = false;
        m.nudgeTitle = 'Build a 2nd stack';
        m.nudgeSub = 'each stack prices independently';

        if (stack && saving > 0) {
          m.locked = false;
          m.kicker = compact ? 'You saved' : 'Saved on this stack';
          m.amount = saving;
          m.pct = Math.round((saving / listTotal) * 100);
          m.strike = listTotal;
          m.proof = compact
            ? `→ ${this.formatMoney(stack.price)}`
            : `list · ${this.formatMoney(stack.price)} in cart`;
        } else {
          m.kicker = 'In your cart';
          m.amount = listTotal;
          m.proof = compact
            ? 'added singly'
            : `${count} item${count === 1 ? '' : 's'} added individually`;
        }
        return m;
      }

      if (!stack) {
        const target = nextTier || this.stacks[0];
        const projected = this.projectedSavingAt(target);
        // Measured against the tier we are actually heading for, not against
        // the smallest one. With non-contiguous tiers (3 and 5, no 4) the old
        // minItems arithmetic went to zero at 4 items and left the customer
        // holding a stack with "Add 0 more items" on a disabled button.
        const remaining = Math.max(target.count - count, 0);
        m.remaining = remaining;

        m.amount = projected !== null && projected > 0 ? projected : target.price;
        m.pct =
          projected !== null && projected > 0
            ? Math.round((projected / (projected + target.price)) * 100)
            : null;

        if (count === 0) {
          // State 2 — sell the size of the prize before any commitment.
          m.state = 'empty';
          m.kicker = 'Save up to';
          // "3 for" / "₹2,299" wraps cleanly; "any 3 →" leaves a dangling arrow.
          m.proof = compact
            ? `${target.count} for ${this.formatMoney(target.price)}`
            : `on any ${target.count} · flat ${this.formatMoney(target.price)}`;
          m.announce = `Any ${target.count} for ${this.formatMoney(target.price)}`;
          m.ctaFull = `Pick ${this.minItems} to unlock`;
          m.ctaShort = `Pick ${this.minItems}`;
        } else {
          // State 3 — price the next item, not the progress bar.
          m.state = 'partial';
          m.kicker = compact
            ? `+${remaining} unlocks`
            : `${remaining} more unlock${remaining === 1 ? 's' : ''}`;
          // No strike on mobile. Desktop can afford "in stack · add any ₹999
          // pick →" to explain what the struck figure is; at half width it
          // becomes a bare number sitting beside the projected saving — two
          // near-identical figures, one struck, reading as a price cut from
          // ₹1,199 to ₹2,299. Nothing is being reduced here yet, so the honest
          // mobile line is just the target.
          m.strike = compact ? null : listTotal;
          m.proof = compact
            ? `→ ${this.formatMoney(target.price)}`
            : `in stack · add any ${this.formatMoney(topAvailable)} pick → ${this.formatMoney(target.price)}`;
          m.announce = `Add ${remaining} more to reach ${target.count} items for ${this.formatMoney(target.price)}`;
          m.ctaFull = `Add ${remaining} more item${remaining === 1 ? '' : 's'}`;
          m.ctaShort = `${remaining} more`;

          const gap = this.costToReachTier();
          if (gap !== null && projected !== null && projected > 0) {
            m.nudgeTitle = `${this.ordinal(target.count)} item costs ${this.formatMoney(gap)}`;
            m.nudgeSub = `worth ${this.formatMoney(topAvailable)} · unlocks ${this.formatMoney(projected)} off`;
          }
        }
        return m;
      }

      if (saving > 0) {
        // States 4, 5 & 6 — the reveal and everything after it.
        m.state = count >= this.maxItems ? 'complete' : count === this.minItems ? 'threshold' : 'mid';
        m.locked = false;
        m.kicker = 'You save';
        m.amount = saving;
        m.pct = Math.round((saving / listTotal) * 100);
        m.strike = listTotal;
        m.proof = compact
          ? `→ ${this.formatMoney(stack.price)}`
          : `list → ${this.formatMoney(stack.price)} bundle`;
        m.ctaFull = `Add to cart · ${this.formatMoney(stack.price)}`;
        m.ctaShort = `Add · ${this.formatMoney(stack.price)}`;
        m.ctaDisabled = false;

        if (count >= this.maxItems) {
          m.nudgeTitle = 'Stack full · max saving';
          m.nudgeSub = 'biggest per-item discount we offer';
          m.nudgeStatic = true;
        } else if (nextTier) {
          const marginal = this.marginalCost();
          const projected = this.projectedSavingAt(nextTier);
          m.nudgeTitle = `Add a ${this.ordinal(nextTier.count)} for ${this.formatMoney(marginal)}`;
          m.nudgeSub =
            projected !== null && projected > 0
              ? `worth ${this.formatMoney(topAvailable)} · saving → ${this.formatMoney(projected)}`
              : `worth ${this.formatMoney(topAvailable)}`;
        }
        return m;
      }

      // State 7 — the bundle costs more than buying these items singly.
      // Charging a gym-goer extra for the privilege of a "bundle" is the
      // fastest way to lose him; we offer the cheaper route instead.
      m.state = 'cheapest';
      m.kicker = 'Cheapest path';
      m.amount = listTotal;
      m.pct = null;
      m.strike = null;
      m.proof = compact
        ? `vs ${this.formatMoney(stack.price)}`
        : `these ${count} cost less than the ${this.formatMoney(stack.price)} bundle`;
      // Without this the announcer fell through to the generic tier line and
      // read out the bundle price — the one figure the customer is not paying.
      m.announce = `These ${count} items cost ${this.formatMoney(
        listTotal
      )} bought individually, less than the ${this.formatMoney(stack.price)} bundle`;
      m.ctaFull = `Add ${count} singly · ${this.formatMoney(listTotal)}`;
      m.ctaShort = `Buy · ${this.formatMoney(listTotal)}`;
      m.ctaDisabled = false;

      const swap = this.bestSwap();
      if (swap) {
        m.nudgeTitle =
          swap.swaps === 1
            ? `Swap in a ${this.formatMoney(swap.pick)} pick`
            : `Swap in ${swap.swaps} ${this.formatMoney(swap.pick)} picks`;
        m.nudgeSub = `starts your bundle saving at ${this.formatMoney(swap.saving)}`;
      }
      return m;
    }

    /**
     * Static mode. The tier's bundle product carries the price, so the bar has
     * exactly one thing to say: which flat price this many items buys, and
     * whether the count is there yet. No savings figure, no percentage, no
     * struck total, no count-up, no marginal pricing on the cards — none of
     * that is an argument the bar needs to make once the number on screen is
     * simply the price of the thing being added.
     *
     * The suggestion box survives only where it is a control rather than a
     * pitch: escaping a losing combination, and starting a second stack.
     */
    staticModel(m, inCart) {
      const count = m.count;
      const listTotal = m.listTotal;
      const stack = m.stack;
      const compact = this.isCompact();
      const target = stack || this.nextStackAfter(count) || this.stacks[0];

      if (inCart) {
        // State 8. The stack is a single indivisible line now, so the slots and
        // the cards it fills both lock; edits happen in the cart.
        const bundled = Boolean(this.bundle);
        m.state = 'postadd';
        m.slotMode = 'locked';
        m.locked = false;
        m.kicker = 'In your cart';
        m.amount = bundled ? this.bundle.price : listTotal;
        m.proof = bundled
          ? compact
            ? `${count} items · flat`
            : `${count} items · one flat price`
          : compact
          ? 'added singly'
          : `${count} item${count === 1 ? '' : 's'} added individually`;
        m.announce = `${count} items in your cart for ${this.formatMoney(m.amount)}`;
        m.ctaFull = 'Go to cart →';
        m.ctaShort = 'Go to cart';
        m.ctaDisabled = false;
        m.nudgeTitle = 'Build a 2nd stack';
        m.nudgeSub = 'each stack prices independently';
        return m;
      }

      // The cheapest path is kept in static mode too. A flat price is still a
      // worse deal when it beats nothing, and a bundle product cannot discount
      // itself out of that — so we offer the singles instead of quietly
      // charging more for the word "bundle".
      if (stack && listTotal <= stack.price) {
        m.state = 'cheapest';
        m.kicker = 'Cheapest path';
        m.amount = listTotal;
        m.proof = compact
          ? `vs ${this.formatMoney(stack.price)}`
          : `these ${count} cost less than the ${this.formatMoney(stack.price)} stack`;
        m.announce = `These ${count} items cost ${this.formatMoney(
          listTotal
        )} bought individually, less than the ${this.formatMoney(stack.price)} stack`;
        m.ctaFull = `Add ${count} singly · ${this.formatMoney(listTotal)}`;
        m.ctaShort = `Buy · ${this.formatMoney(listTotal)}`;
        m.ctaDisabled = false;

        const swap = this.bestSwap();
        if (swap) {
          m.nudgeTitle =
            swap.swaps === 1
              ? `Swap in a ${this.formatMoney(swap.pick)} pick`
              : `Swap in ${swap.swaps} ${this.formatMoney(swap.pick)} picks`;
          m.nudgeSub = `puts you ${this.formatMoney(swap.saving)} ahead on the stack`;
        }
        return m;
      }

      if (stack) {
        // The count matches a tier: this is the price, ready to buy.
        m.state =
          count >= this.maxItems ? 'complete' : count === this.minItems ? 'threshold' : 'mid';
        m.locked = false;
        m.kicker = 'Your stack';
        m.amount = stack.price;
        m.proof = compact ? `${count} items · flat` : `${count} items · one flat price`;
        m.announce = `${count} items for ${this.formatMoney(stack.price)}`;
        m.ctaFull = `Add to cart · ${this.formatMoney(stack.price)}`;
        m.ctaShort = `Add · ${this.formatMoney(stack.price)}`;
        m.ctaDisabled = false;
        return m;
      }

      // Below the first tier, or sitting in a gap between two configured ones.
      const remaining = Math.max(target.count - count, 0);
      m.remaining = remaining;
      m.amount = target.price;

      if (count === 0) {
        m.state = 'empty';
        m.kicker = compact ? `Any ${target.count}` : `Any ${target.count} for`;
        m.proof = compact ? 'one flat price' : 'one flat price, every combination';
        m.announce = `Any ${target.count} items for ${this.formatMoney(target.price)}`;
        m.ctaFull = `Pick ${target.count} to unlock`;
        m.ctaShort = `Pick ${target.count}`;
      } else {
        m.state = 'partial';
        m.kicker = compact
          ? `+${remaining} unlocks`
          : `${remaining} more unlock${remaining === 1 ? 's' : ''}`;
        m.proof = compact ? `${target.count} items` : `${target.count} items · one flat price`;
        m.announce = `Add ${remaining} more to reach ${
          target.count
        } items for ${this.formatMoney(target.price)}`;
        m.ctaFull = `Add ${remaining} more item${remaining === 1 ? '' : 's'}`;
        m.ctaShort = `${remaining} more`;
      }

      return m;
    }

    renderSlots(model, opts) {
      this.slots.forEach((slot, index) => {
        const item = this.selection[index];
        const img = slot.querySelector('[data-slot-image]');
        const removeBtn = slot.querySelector('[data-box-slot-remove]');
        const lockedTag = slot.querySelector('[data-slot-locked]');
        const emptyLabel = slot.querySelector('[data-slot-empty-label]');

        if (item) {
          slot.dataset.variantId = item.variantId;
          slot.classList.add('is-filled');
          slot.setAttribute('aria-label', `Slot ${index + 1}: ${item.title}`);

          if (img) {
            if (item.image && img.getAttribute('src') !== item.image) img.src = item.image;
            img.hidden = false;
          }
          if (emptyLabel) emptyLabel.hidden = true;

          // Once in the cart the slot is locked: edits happen in the cart now.
          const locked = model.slotMode === 'locked';
          if (removeBtn) {
            removeBtn.hidden = locked;
            removeBtn.setAttribute('aria-label', `Remove ${item.title} from stack`);
          }
          if (lockedTag) lockedTag.hidden = !locked;

          if (opts.added && opts.filledIndex === index && !prefersReducedMotion()) {
            slot.classList.remove('just-filled');
            void slot.offsetWidth;
            slot.classList.add('just-filled');
          }
        } else {
          delete slot.dataset.variantId;
          slot.classList.remove('is-filled', 'just-filled');
          slot.setAttribute('aria-label', `Slot ${index + 1} of ${this.slots.length}, empty`);
          if (img) {
            img.hidden = true;
            img.removeAttribute('src');
          }
          if (removeBtn) removeBtn.hidden = true;
          if (lockedTag) lockedTag.hidden = true;
          if (emptyLabel) emptyLabel.hidden = false;
        }
      });
    }

    renderCards(model) {
      // Static mode prices nothing per-card: the tier price is not built from
      // the picks, so a "this item costs you X extra" figure would be invented.
      const marginal = this.staticMode ? null : this.marginalCost();
      const showMarginal = marginal !== null && model.state !== 'postadd';
      // Once the stack is bought its own cards are frozen, but the rest stay
      // live: picking one starts stack #2 rather than doing nothing.
      const locked = this.isCommitted();
      const full = !locked && this.selection.length >= this.maxItems;
      const nextOrdinal = this.ordinal(this.selection.length + 1);

      this.cards.forEach((card) => {
        const selected = this.isSelected(card.dataset.variantId);
        const badge = card.querySelector('[data-box-card-marginal]');
        const value = card.querySelector('[data-box-card-marginal-value]');
        const asEl = card.querySelector('[data-box-card-marginal-as]');
        const aloneEl = card.querySelector('[data-box-card-marginal-alone]');
        const inStack = card.querySelector('[data-box-card-instack]');
        const inStackLabel = card.querySelector('[data-box-card-instack-label]');
        const btnLabel = card.querySelector('[data-btn-label]');
        const btn = card.querySelector('[data-box-add-btn]');

        card.classList.toggle('is-in-stack', selected);
        card.classList.toggle('is-marginal', !selected && showMarginal && !full);
        card.classList.toggle('is-stack-full', full);
        card.classList.toggle('is-locked', selected && locked);

        if (badge) badge.hidden = selected || !showMarginal || full;
        if (inStack) inStack.hidden = !selected;

        if (selected && inStackLabel) {
          inStackLabel.textContent = locked
            ? `In your cart · slot ${this.slotIndexOf(card.dataset.variantId) + 1}`
            : `In slot ${this.slotIndexOf(card.dataset.variantId) + 1} of ${this.maxItems}`;
        }

        if (!selected && showMarginal && !full && value) {
          value.textContent = this.formatMoney(marginal);
          if (asEl) asEl.textContent = `as your ${nextOrdinal} item`;
          if (aloneEl) aloneEl.textContent = `${this.formatMoney(this.priceOf(card))} alone`;
        }

        if (btnLabel) {
          if (selected && locked) btnLabel.textContent = 'In cart';
          else if (selected) btnLabel.textContent = 'Remove';
          else if (full) btnLabel.textContent = 'Stack full';
          else if (showMarginal) btnLabel.textContent = `Add for ${this.formatMoney(marginal)}`;
          else btnLabel.textContent = this.addBtnLabel;
        }

        if (btn) {
          btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
          btn.setAttribute(
            'aria-disabled',
            (selected && locked) || (!selected && full) ? 'true' : 'false'
          );
        }
      });
    }

    renderPlate(model, opts) {
      if (!this.plate) return;

      this.plate.dataset.plateState = model.state;

      if (this.plateKicker) this.plateKicker.textContent = model.kicker;
      if (this.plateLocked) this.plateLocked.hidden = !model.locked;

      // 60ms — border-style cannot tween, so it switches on its own beat.
      const wasUnlocked = this.plate.classList.contains('is-unlocked');
      const flip = () => this.plate.classList.toggle('is-unlocked', !model.locked);
      if (!model.locked && !wasUnlocked && !opts.initial) {
        this.after(T_PLATE_FLIP, flip);
      } else {
        flip();
      }

      if (this.platePct) {
        const show = this.showPct && model.pct !== null && model.pct > 0;
        this.platePct.hidden = !show;
        if (show) this.platePct.textContent = `${model.pct}%`;
      }

      if (this.plateStrike) {
        if (model.strike !== null) {
          this.plateStrike.textContent = this.formatMoney(model.strike);
          this.plateStrike.hidden = false;
          if (opts.added && !prefersReducedMotion()) {
            this.plateStrike.classList.remove('just-entered');
            void this.plateStrike.offsetWidth;
            this.plateStrike.classList.add('just-entered');
          }
        } else {
          this.plateStrike.hidden = true;
        }
      }

      if (this.plateProofText) this.plateProofText.textContent = model.proof;

      if (model.amount === null) return;

      // Static mode states a price rather than revealing a saving, so the
      // figure lands in one frame instead of counting up to itself.
      const duration =
        this.staticMode || opts.initial
          ? 0
          : opts.removed
          ? COUNT_REMOVE_MS
          : this._prevState === model.state
          ? COUNT_UPDATE_MS
          : COUNT_REVEAL_MS;

      this.after(this.staticMode || opts.initial || opts.removed ? 0 : T_COUNT_START, () =>
        this.countTo(model.amount, duration)
      );
    }

    renderCta(model, opts) {
      const short =
        this.isTiny() || this._ctaCondensed ? this.tinyCtaLabel(model) : model.ctaShort;

      const apply = () => {
        this.cartBtn.disabled = model.ctaDisabled;
        if (this.cartBtnLabel) this.cartBtnLabel.textContent = model.ctaFull;
        if (this.cartBtnLabelShort) this.cartBtnLabelShort.textContent = short;
        // The visible label is the short one below 1280px; keep the accessible
        // name on the full string so the price is never lost to a screen reader.
        this.cartBtn.setAttribute('aria-label', model.ctaFull);
      };

      // 240ms — the CTA arms only after the number has begun to land.
      if (opts.added && !model.ctaDisabled) this.after(T_CTA_ARM, apply);
      else apply();
    }

    renderNudge(model, opts) {
      if (!this.nudge) return;

      // In static mode the box is only shown where it does something: start a
      // second stack, or get out of a combination that costs more than it
      // saves. It never appears as an upsell, because there is no marginal
      // figure in this mode to upsell with.
      const stateAllowed =
        !this.staticMode || model.state === 'postadd' || model.state === 'cheapest';
      const show = stateAllowed && this.nudgeAllowed() && Boolean(model.nudgeTitle);
      const changed = this.nudgeTitle && this.nudgeTitle.textContent !== (model.nudgeTitle || '');

      const apply = () => {
        this.nudge.hidden = !show;
        this.nudge.classList.toggle('is-static', model.nudgeStatic);
        if (model.nudgeStatic) this.nudge.setAttribute('tabindex', '-1');
        else this.nudge.removeAttribute('tabindex');

        if (show) {
          if (this.nudgeTitle) this.nudgeTitle.textContent = model.nudgeTitle;
          if (this.nudgeSub) this.nudgeSub.textContent = model.nudgeSub || '';
          if (opts.added && changed && !prefersReducedMotion()) {
            this.nudge.classList.remove('just-entered');
            void this.nudge.offsetWidth;
            this.nudge.classList.add('just-entered');
          }
        }
      };

      // 300ms — enters after the saving has settled so it never competes.
      if (opts.added) this.after(T_NUDGE_IN, apply);
      else apply();
    }

    countTo(target, duration) {
      if (!this.plateAmount) return;
      this.cancelCountUp();

      if (!duration || prefersReducedMotion()) {
        this._displayedAmount = target;
        this.plateAmount.textContent = this.formatMoney(target);
        return;
      }

      const from = this._displayedAmount || 0;
      const delta = target - from;
      if (delta === 0) {
        this.plateAmount.textContent = this.formatMoney(target);
        return;
      }

      const start = performance.now();
      const step = (now) => {
        const t = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out
        this.plateAmount.textContent = this.formatMoney(Math.round(from + delta * eased));
        if (t < 1) {
          this._countUpFrame = requestAnimationFrame(step);
        } else {
          this._displayedAmount = target;
          this._countUpFrame = null;
        }
      };
      this._countUpFrame = requestAnimationFrame(step);
    }

    cancelCountUp() {
      if (this._countUpFrame) {
        cancelAnimationFrame(this._countUpFrame);
        this._countUpFrame = null;
      }
    }

    /** The mobile bar has ~200px for the whole plate, so copy compresses. */
    isCompact() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    /**
     * Whether the suggestion box may show at the current width. Shares the
     * compact breakpoint with the rest of the bar so there is one definition of
     * "mobile", and the existing matchMedia listeners keep it in sync on resize.
     */
    nudgeAllowed() {
      return this.isCompact() ? this.showNudgeMobile : this.showNudgeDesktop;
    }

    /** Below the design's 360px floor, degrade rather than overflow. */
    isTiny() {
      return window.matchMedia('(max-width: 359px)').matches;
    }

    /**
     * On the narrowest phones the price leaves the CTA — it is already on the
     * proof line immediately to its left, so nothing is actually lost.
     */
    tinyCtaLabel(model) {
      switch (model.state) {
        case 'empty':
          return `Pick ${this.minItems}`;
        case 'partial':
          return `${model.remaining} more`;
        case 'cheapest':
          return 'Buy';
        case 'postadd':
          return 'Cart';
        default:
          return 'Add';
      }
    }

    ordinal(n) {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }

    /**
     * A failed add used to write only into the visually hidden status region,
     * so a sighted customer saw the button flick back to its old label and
     * nothing else. Static mode makes this load-bearing: the likeliest
     * misconfiguration — a bundle product that is not purchasable on the Online
     * Store channel — surfaces as a 422 and nothing but this.
     */
    showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
      // Announced separately from the polite status region so it is not lost
      // behind the debounce.
      if (this.statusEl) this.statusEl.textContent = message;
      if (this.nudge) this.nudge.hidden = true;
    }

    clearError() {
      if (this.errorEl && !this.errorEl.hidden) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }

    /** One polite region, debounced, so five rapid adds announce once. */
    announce(model) {
      if (!this.statusEl) return;
      clearTimeout(this._announceTimer);
      this._announceTimer = setTimeout(() => {
        const parts = [];
        if (model.amount !== null) parts.push(`${model.kicker} ${this.formatMoney(model.amount)}`);
        // Prefer the spoken form: the compact proof is shorthand like
        // "→ ₹2,299", which a screen reader cannot make sense of.
        if (model.announce) {
          parts.push(model.announce);
        } else if (model.count > 0 && model.stack) {
          parts.push(`${model.count} items, ${this.formatMoney(model.stack.price)} total`);
        } else if (model.proof) {
          parts.push(model.proof);
        }
        // Hidden means hidden: a screen reader should not hear a box that is
        // switched off at this width, or suppressed by the bundle mode.
        const nudgeSpoken =
          !this.staticMode || model.state === 'postadd' || model.state === 'cheapest';
        if (model.nudgeTitle && nudgeSpoken && this.nudgeAllowed()) parts.push(model.nudgeTitle);
        this.statusEl.textContent = parts.join('. ');
      }, ANNOUNCE_DEBOUNCE_MS);
    }

    // ---------- layout ----------

    observeBar() {
      if (!this.bar) return;

      const apply = () => {
        const h = Math.ceil(this.bar.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--a2g-bar-h', `${h}px`);
        this.positionWidget(h);
      };

      apply();
      document.documentElement.classList.add('box-builder-active');
      if (this.root.dataset.widgetSelector) {
        document.documentElement.setAttribute('data-box-widget-selector', '');
      }

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(apply);
        this._resizeObserver.observe(this.bar);
      } else {
        window.addEventListener('resize', apply);
      }

      // Copy length differs between the compact and full bar, so the plate has
      // to be rebuilt when the breakpoint changes, not only on load.
      const onChange = () => this.render({ initial: true });
      ['(max-width: 749px)', '(max-width: 359px)'].forEach((query) => {
        const mq = window.matchMedia(query);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else if (mq.addListener) mq.addListener(onChange);
      });

      this.watchForWidget();
    }

    /**
     * The chat widget is injected by an app, so its own stylesheet often wins
     * on specificity and it can mount long after this script runs. Setting the
     * offset inline with !important, and re-applying when the DOM changes, is
     * the only reliable way to keep it off the CTA.
     */
    positionWidget(barHeight) {
      const selector = this.root.dataset.widgetSelector;
      if (!selector) return;

      let nodes;
      try {
        nodes = document.querySelectorAll(selector);
      } catch (e) {
        return; // Invalid selector configured in the theme editor.
      }

      nodes.forEach((el) => {
        if (this.root.contains(el)) return;
        const style = window.getComputedStyle(el);
        if (style.position !== 'fixed') return;

        if (this.isCompact()) {
          // Lift it clear of the bar entirely.
          el.style.setProperty(
            'bottom',
            `calc(${barHeight}px + env(safe-area-inset-bottom, 0px) + 12px)`,
            'important'
          );
        } else {
          // Centre it in the gutter the bar reserves on the right.
          el.style.setProperty('bottom', `${Math.max((barHeight - 56) / 2, 8)}px`, 'important');
          el.style.setProperty('right', '24px', 'important');
        }
      });
    }

    watchForWidget() {
      if (!this.root.dataset.widgetSelector || !('MutationObserver' in window)) return;

      const reapply = () => {
        const h = Math.ceil(this.bar.getBoundingClientRect().height);
        this.positionWidget(h);
      };

      const observer = new MutationObserver(reapply);
      observer.observe(document.body, { childList: true, subtree: true });

      // App embeds settle within a few seconds; stop watching after that so we
      // are not running a callback on every DOM mutation for the whole session.
      setTimeout(() => observer.disconnect(), 15000);
    }

    // ---------- cart ----------

    handleCta() {
      const model = this.buildModel();
      if (model.ctaDisabled) return;

      if (model.state === 'postadd') {
        this.goToCart();
        return;
      }

      // On the cheapest path the items are added as ordinary line items with
      // no bundle properties, so no bundle price is ever applied — in either
      // mode. Static mode otherwise adds the tier's own bundle product.
      if (this.staticMode && model.state !== 'cheapest') {
        this.addBundleToCart();
        return;
      }

      this.addToCart(model.state === 'cheapest');
    }

    cartUrl() {
      return window.themeVariables?.routes?.cartUrl || `${window.Shopify?.routes?.root || '/'}cart`;
    }

    cartAddUrl() {
      return `${
        window.themeVariables?.routes?.cartAddUrl ||
        `${window.Shopify?.routes?.root || '/'}cart/add`
      }.js`;
    }

    goToCart() {
      const cartUrl = this.cartUrl();

      if (window.themeVariables?.settings?.cartType === 'page') {
        window.location.href = cartUrl;
        return;
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer && window.themeVariables?.settings?.cartType === 'drawer') {
        drawer.clientWidth;
        drawer.open = true;
      } else {
        window.location.href = cartUrl;
      }
    }

    /** Shared "Adding…" bookkeeping. Returns the labels to put back on failure. */
    beginCartRequest() {
      // Our own add dispatches cart:updated / cart:refresh; ignore those so a
      // sync cannot race the state we are in the middle of writing.
      this._syncing = true;
      this.clearError();
      this.cartBtn.disabled = true;

      const previous = {
        full: this.cartBtnLabel ? this.cartBtnLabel.textContent : '',
        short: this.cartBtnLabelShort ? this.cartBtnLabelShort.textContent : '',
      };
      if (this.cartBtnLabel) this.cartBtnLabel.textContent = 'Adding…';
      if (this.cartBtnLabelShort) this.cartBtnLabelShort.textContent = 'Adding…';
      return previous;
    }

    restoreCtaLabels(previous) {
      if (this.cartBtnLabel) this.cartBtnLabel.textContent = previous.full;
      // The short label is the visible one below 1280px. Leaving it on
      // "Adding…" after a failure is what made a failed add look like a hang.
      if (this.cartBtnLabelShort) this.cartBtnLabelShort.textContent = previous.short;
    }

    /** Hand the fresh cart to the drawer / cart page the way the theme expects. */
    async refreshCart(responseJson) {
      const cartUrl = this.cartUrl();

      if (window.themeVariables?.settings?.cartType === 'page') {
        window.location.href = cartUrl;
        return;
      }

      const cartContent = await (await fetch(`${cartUrl}.js`)).json();

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartContent } })
      );

      cartContent.sections = responseJson.sections;

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: window.themeVariables?.settings?.cartType === 'drawer',
          },
        })
      );
    }

    /**
     * Static mode. One line: the tier's bundle product at its own price, with
     * the customer's picks attached as visible line-item properties so the cart
     * drawer reads "Item 1: Creatine Black" and so on. Both cart templates
     * already render visible properties and skip the _ prefixed ones, so the
     * private _BoxId that lets sync find this exact line stays out of sight.
     */
    async addBundleToCart() {
      const stack = this.stackFor(this.selection.length);

      if (!stack || !stack.bundleVariantId) {
        this.showError('This stack size is not available right now. Please try another.');
        return;
      }

      const previous = this.beginCartRequest();

      const boxId = `box-${Date.now()}`;
      const formData = new FormData();
      formData.append('id', String(stack.bundleVariantId));
      formData.append('quantity', 1);
      this.selection.forEach((item, index) => {
        formData.append(`properties[Item ${index + 1}]`, item.title);
      });
      formData.append('properties[_BoxId]', boxId);
      formData.append('sections', 'mini-cart');

      try {
        const response = await fetch(this.cartAddUrl(), {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(
            responseJson.description || responseJson.message || 'Could not add your stack to cart'
          );
        }

        this.bundle = {
          boxId,
          variantId: String(stack.bundleVariantId),
          price: stack.price,
          count: this.selection.length,
        };
        this.saveBundle();

        document.dispatchEvent(
          new CustomEvent('box-builder:added', {
            detail: {
              boxId,
              items: this.selection.slice(),
              bundled: true,
              bundleVariantId: stack.bundleVariantId,
              mode: 'static',
            },
          })
        );

        this.render();
        await this.refreshCart(responseJson);
      } catch (err) {
        this.restoreCtaLabels(previous);
        this.render();
        // After render, so the render's own clearError cannot wipe it.
        this.showError(err.message || 'Something went wrong adding your stack.');
      } finally {
        this._syncing = false;
      }
    }

    async addToCart(asSingles) {
      const newItems = this.getNewItems();
      if (newItems.length === 0) {
        this.goToCart();
        return;
      }

      const previous = this.beginCartRequest();

      const boxId = `box-${Date.now()}`;
      const formData = new FormData();
      newItems.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.variantId);
        formData.append(`items[${index}][quantity]`, 1);
        if (!asSingles) {
          formData.append(`items[${index}][properties][_Box]`, 'Custom Box');
          formData.append(`items[${index}][properties][_BoxId]`, boxId);
        }
      });
      formData.append('sections', 'mini-cart');

      try {
        const response = await fetch(this.cartAddUrl(), {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(
            responseJson.description || responseJson.message || 'Could not add items to cart'
          );
        }

        newItems.forEach((item) => {
          const id = String(item.variantId);
          if (!this.addedToCart.includes(id)) this.addedToCart.push(id);
          // Remember which box the line belongs to so cart sync can match the
          // exact line later. Singles carry no properties, so nothing to store.
          const entry = this.selection.find((s) => String(s.variantId) === id);
          if (entry && !asSingles) entry.boxId = boxId;
        });
        this.saveAddedToCart();
        this.saveState();

        document.dispatchEvent(
          new CustomEvent('box-builder:added', {
            detail: { boxId, items: newItems, bundled: !asSingles },
          })
        );

        this.render();
        await this.refreshCart(responseJson);
      } catch (err) {
        this.restoreCtaLabels(previous);
        this.render();
        // After render, so the render's own clearError cannot wipe it.
        this.showError(err.message || 'Something went wrong adding your stack.');
      } finally {
        this._syncing = false;
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-box-builder]').forEach((root) => {
      if (!root._boxBuilderInstance) {
        root._boxBuilderInstance = new BoxBuilder(root);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
